import React, { memo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';

import AppStyles from '../AppStyles';

import CustomText from './CustomText';

/**
 * Badge - Organization badge (gold pill) for V3 design system
 *
 * Memoized to prevent unnecessary re-renders in lists.
 *
 * @param {string} label - Text to display in the badge
 * @param {string} icon - Optional Feather icon name to display
 * @param {string} variant - Badge variant: 'primary' | 'secondary' | 'muted'
 * @param {object} style - Additional styles for the badge
 */
const Badge = memo(function Badge({ label, icon, variant = 'primary', style }) {
  const getVariantStyle = () => {
    switch (variant) {
      case 'secondary':
        return styles.secondary;
      case 'muted':
        return styles.muted;
      default:
        return styles.primary;
    }
  };

  const getTextColor = () => {
    switch (variant) {
      case 'secondary':
        return AppStyles.colorSet.brandPrimary;
      case 'muted':
        return AppStyles.colorSet.textSecondary;
      default:
        return '#000000';
    }
  };

  return (
    <View style={[styles.badge, getVariantStyle(), style]}>
      {icon && (
        <Feather
          name={icon}
          size={14}
          color={getTextColor()}
          style={styles.icon}
        />
      )}
      <CustomText font="medium" size={14} color={getTextColor()}>
        {label}
      </CustomText>
    </View>
  );
});

const styles = StyleSheet.create({
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 16,
    alignSelf: 'flex-start',
  },
  primary: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  secondary: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: AppStyles.colorSet.brandPrimary,
  },
  muted: {
    backgroundColor: AppStyles.colorSet.inputBackground,
  },
  icon: {
    marginRight: 6,
  },
});

export default Badge;
